import { useEffect, useMemo } from "react";
import { useParams } from "react-router";

import { FilterButton, MasterDetailLayout } from "common/components";
import { getPageId } from "common/utilities";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { activateModal } from "domains/modal/actions";
import { KnowledgeDetail } from "domains/publication/components/KnowledgeDetail";
import { KnowledgeList } from "domains/publication/components/KnowledgeList";
import { getTopicPageEvent, getTopicRelatedItemDetailPageEvent } from "domains/topic/analytics";
import { getTopicTitle, useTopicNavigation } from "domains/topicDetail/utils";
import { TopicSearchBar } from "domains/topicSearchFilter/components/TopicSearchBar";
import { useTopicContentMeta } from "domains/topicSearchFilter/useTopicContentMeta";
import { applyVideoSearchCriteria } from "domains/videos/actions";
import {
  createVideosForTopicSelector,
  getIsVideosFiltered,
  getVideoSearchTerm,
  getVideosFilterCount,
} from "domains/videos/selectors";
import { createKnowledgesSelector, getKnowledgeById } from "entities/knowledge/selectors";
import { getCanSeeSectionContent } from "entities/permissions/selectors";
import { fetchTopic } from "entities/topics/actions";
import {
  createHasUnauthenticatedContentAccessSelector,
  createTopicSelector,
  getTopic,
} from "entities/topics/selectors";

export const TopicVideoPage = () => {
  const dispatch = useDispatch();
  const { keyIssueId, knowledgeId, topicId } = useParams();
  const hasAccessToContent = useSelector(getCanSeeSectionContent);
  const isFiltered = useSelector(getIsVideosFiltered);
  const search = useSelector(getVideoSearchTerm);
  const filterCount = useSelector(getVideosFilterCount);
  const { hasFilters } = useTopicContentMeta("videos");
  const track = useTrack();
  const translate = useTranslate();

  if (topicId === undefined) throw new Error("topicId param undefined in TopicVideoPage");
  const selectedTopicId = keyIssueId ?? topicId;

  const unauthenticatedSectionAccess = useSelector(createHasUnauthenticatedContentAccessSelector(topicId));
  const topic = useSelector(createTopicSelector(topicId));
  const videosForTopic = useSelector(createVideosForTopicSelector(selectedTopicId));
  const keyIssue = useSelector(getTopic(keyIssueId));
  const { path } = useTopicNavigation({ id: topicId, keyIssueId });

  const knowledgesSelector = useMemo(
    () => createKnowledgesSelector(videosForTopic?.data ?? []),
    [videosForTopic?.data],
  );

  const videos = useSelector(knowledgesSelector);

  const id = getPageId(knowledgeId, videos);

  const knowledge = useSelector(getKnowledgeById(id));

  const hasNextPage = videosForTopic?.hasNextPage ?? false;
  const isPending = videosForTopic?.isFetching ?? false;
  const isLocked = !hasAccessToContent && !unauthenticatedSectionAccess;
  const selectedTopic = keyIssueId ? keyIssue : topic;

  useEffect(() => {
    if (!topic || (keyIssueId && !keyIssue)) {
      return;
    }
    const event = getTopicPageEvent(topic, keyIssue, "Video List");
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyIssue?.id, topic?.id, track]);

  useEffect(() => {
    if (!selectedTopic || !knowledge) {
      return;
    }
    const event = getTopicRelatedItemDetailPageEvent(
      "Video detail",
      { id: knowledge.id, name: knowledge.title },
      "Video",
      selectedTopic,
    );
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledge?.id, selectedTopic?.id, track]);

  useEffect(() => {
    if (videos.length === 0 || isPending) {
      dispatch(
        applyVideoSearchCriteria({
          topicId: selectedTopicId,
          offset: 0,
          limit: 20,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId, hasAccessToContent]);

  useEffect(() => {
    if (!selectedTopic) {
      dispatch(fetchTopic({ id: topicId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  const getLink = (knowledgeId: string) => `${path}/videos/${knowledgeId}`;

  const loadNextPage = () => {
    dispatch(
      applyVideoSearchCriteria({
        topicId: selectedTopicId,
        offset: videos.length,
        limit: 20,
      }),
    );
  };

  let emptyMessage = isFiltered
    ? translate("app.monitor.empty_message_filter")
    : `${translate("app.topic.no_content")} ${translate("app.discover.topic.tabs.videos.title")}.`;

  if (!topic?.id) {
    emptyMessage = translate("app.no_access.topic_content");
  }

  const knowledgeList = (
    <KnowledgeList
      items={videos}
      selectedId={id}
      trackPage="Video List"
      getToLink={getLink}
      type="medium"
      loadNextPage={!isLocked ? loadNextPage : undefined}
      hasNextPage={!isLocked ? hasNextPage : false}
      isPending={isPending}
      lockTiles={isLocked}
      emptyMessage={emptyMessage}
    />
  );

  const shareLink = knowledge
    ? `${import.meta.env.VITE_WEBSITE_URL}/topics/${topicId}/videos/${knowledge.id}`
    : undefined;

  const onFilterClick = () => dispatch(activateModal({ id: "TopicVideoFilter", params: { selectedTopicId, topicId } }));

  const topicFilterButton =
    topic?.id && !isLocked && hasFilters ? <FilterButton count={filterCount} onClick={onFilterClick} /> : undefined;

  const onSearchHandle = (search: string) => dispatch(applyVideoSearchCriteria({ topicId: selectedTopicId, search }));

  const searchElement =
    topic?.id && !isLocked ? (
      <TopicSearchBar onSearch={onSearchHandle} isPending={isPending} defaultValue={search} />
    ) : undefined;

  return (
    <MasterDetailLayout
      listTitle={translate("app.discover.topic.tabs.videos.title")}
      listComponent={knowledgeList}
      listBackNavigation={{
        label: getTopicTitle(topic, keyIssue) ?? "",
        path,
      }}
      detailBackNavigation={{
        label: translate("app.discover.topic.tabs.videos.title"),
        path: `${path}/videos`,
      }}
      mainView={knowledgeId ? "right" : "left"}
      listHeaderRightElement={topicFilterButton}
      headerElement={searchElement}
    >
      <KnowledgeDetail
        id={id}
        knowledge={knowledge}
        shareLink={shareLink}
        topicBack={{ term: "app.discover.topic.tabs.videos.title" }}
      />
    </MasterDetailLayout>
  );
};
